import { Alert, AppBar, Box, Button, CircularProgress, Divider, Paper, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../services/HttpService';
import { CompanyContact, newCompanyContact, CompanyAddress, newCompanyAddress, newAddress, Address } from '../../../../model';
import { CompanyForm, CompanyFormRef } from '../../../../model/forms/CompanyForm';
import { TabPanel, TabProps } from '../../../../components/ui/TabPanel';
import { DataInfoTable, DataListInfoTable } from '../../../../components/tables/DataTable';
import { Accessgroup } from '../../../../model/System';
import { Company, Organization } from '../../../../model';
import { dayjsService } from '../../../../services/dayjsService';
import { CenterSpinner } from '../../../../components/ui/CenterSpinner';
import { ManyToManyForm, ManyToManyFormRef } from '../ManyToManyForm';
import { AddressForm, AddressFormRef } from '../../../../model/forms/AddressForm';

interface CompanyOverviewProps {
  item: Company;
  getError?: string;
  onClose?: () => void;
  onItemChange?: (item: Company) => void;
}

export const CompanyOverview = (props: CompanyOverviewProps) => {
  const theme = useTheme();
  const { getError, onItemChange } = props;
  const { api, error, isLoading } = useApi();
  const { api: modApi, error: modError, isLoading: modIsLoading } = useApi();
  const [item, setItem] = useState<Company>(props.item);
  const [displayData, setDisplayData] = useState<Record<string, string>>(null);
  const [companyContact, setCompanyContact] = useState<CompanyContact>(newCompanyContact);
  const [companyAddress, setCompanyAddress] = useState<CompanyAddress>(newCompanyAddress);
  const [tabIndex, setTabIndex] = useState(0);

  const addCompanyRef = useRef<CompanyFormRef>(null);
  const addCompanyContactRef = useRef<ManyToManyFormRef>(null);
  const addAddressRef = useRef<AddressFormRef>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!props.item || props.item.id === 0) {
      setTabIndex(1);
    } else {
      getItem();
      setDisplayData({
        "ID": String(props.item.id),
        "Navn": props.item.name,
        "Organisasjonsnummer": props.item.organization_number,
        "brreg_parent_id": String(props.item.brreg_parent_id),
        "Tillat ansatte": props.item.allow_employments ? "Ja" : "Nei",
        "Lønnstrekk instance": props.item.salary_deduction_instance,
        "Primær kontakt": item?.primary_contact?.name,
        "Bedriftsgruppe": item?.company_group?.name,
      });
    }
  }, [props.item]);

  useEffect(() => {
    setDisplayData((prev) => ({
      ...prev,
      "Primær kontakt": item?.primary_contact?.name,
      "Bedriftsgruppe": item?.company_group?.name,
    }));
  }, [item]);

  const getItem = () => {
    api("/manage/company/getCompany", "POST", { id: props.item.id, with_relations: true }).then((res) => {
      if (res.statusCode === 200) {
        const company: Company = res.company;
        setItem(company);
      }
    });

  }

  const reset = () => {
    setItem(() => ({ ...item }));
  }

  const changeTab = (index: number, performChange: boolean = true): boolean => {
    if (!item.id) {
      if (performChange) {
        setTabIndex(1);
      }

      return index === 1
    }

    if (performChange) {
      setTabIndex(index);
    }

    return true;
  }

  const saveItem = () => {
    const companyRef = addCompanyRef.current.getItem();
    console.log("submitting", companyRef, "valid", addCompanyRef.current.valid());

    if (addCompanyRef.current.valid()) {
      api("/manage/company/postCompany", "POST", { ...companyRef }).then(res => {
        console.log("res:", res)
        if (res?.statusCode === 200) {
          onItemChange(res.company);
          setTabIndex(0);
        }
      });
    }
  }

  const saveCompanyContact = () => {
    const newRel = addCompanyContactRef.current.getItem();
    console.log("item", newRel, "valid", addCompanyContactRef.current.valid());

    if (addCompanyContactRef.current.valid()) {
      const isNew = companyContact.id === 0;

      const newRelItem: CompanyContact = {
        ...companyContact,
        company_id: item.id,
        contact_id: newRel.select_id,
      };

      api("/manage/company/postCompanyContact", "POST", newRelItem).then(res => {
        if (res?.statusCode === 200) {
          setCompanyContact(res.company_contact);

          setItem((prev) => ({
            ...prev,
            company_contacts: isNew
              ? [...(prev.company_contacts || []), res.company_contact]
              : prev.company_contacts.map(u =>
                u.id === newRelItem.id ? res.company_contact : u
              ),
          }));
        }
      });
    }
  }

  const saveCompanyAddress = () => {
    const newRel = addAddressRef.current.getItem();
    console.log("item", newRel, "valid", addAddressRef.current.valid());

    if (addAddressRef.current.valid()) {
      const isNew = companyAddress.id === 0;

      const newRelItem: CompanyAddress = {
        ...companyAddress,
        id: companyAddress.id,
        address_id: newRel.id,
        company_id: item.id,
        valid_from: newRel.valid_from,
        valid_to: newRel.valid_to,
        primary_address: newRel.primary,
        addressline1: newRel.addressline1,
        addressline2: newRel.addressline2,
        addressline3: newRel.addressline3,
        postcode: newRel.postcode,
        postoffice: newRel.postoffice,
      };

      api("/manage/company/postCompanyAddress", "POST", newRelItem).then(res => {
        if (res?.statusCode === 200) {
          setCompanyAddress(res.company_address);

          setItem((prev) => ({
            ...prev,
            company_addresses: isNew
              ? [...(prev.company_addresses || []), res.company_address]
              : prev.company_addresses.map(u =>
                u.id === newRelItem.id ? res.company_address : u
              ),
          }));
        }
      });
    }
  }

  return (
    <>
      <AppBar position="static"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? "inherit" : theme.palette.grey[300],
          color: theme.palette.mode === 'dark' ? "white" : "black"
        }}
      >
        {error && <Alert severity='error'>{error}</Alert>}

        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant={"scrollable"}
          textColor="inherit"
          indicatorColor="primary"
          aria-label="manage company tabs"
        >
          {[
            "Oversikt",
            item.id ? "Rediger" : "Opprett",
            "Kontakter",
            "Adresser",
          ].map((name, index) => (
            <Tab disabled={!changeTab(index, false)} key={index} label={name} sx={{ fontWeight: "bold", textTransform: "none" }} {...TabProps(index)} />
          ))}
        </Tabs>
      </AppBar>
      <Box sx={{ mt: 2, p: { xs: 0, md: 3 }, minHeight: 400 }}>
        <TabPanel value={tabIndex} index={0} sx={{ minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: "column", justifyContent: 'center', gap: 3, maxWidth: { xs: "100%", md: 1200 } }}>
            <DataInfoTable data={displayData} onEdit={() => changeTab(1)} />
            <CenterSpinner loading={isLoading}>
              <>
                <DataListInfoTable title={"Kontaktpersoner"}
                  data={item.company_contacts ?? []}
                  displayField="contact_name"
                  onAdd={() => { setCompanyContact(newCompanyContact()); changeTab(2) }}
                  onEdit={(id) => { setCompanyContact(item.company_contacts.find(u => u.id === id)); changeTab(2) }}
                  onDelete={(id) => alert("delete not implemented")}
                  onPrimaryCahnge={(id) => console.log("setting primary to", id)}
                  primaryField='main_contact'
                />
                <DataListInfoTable title={"Adresser"}
                  data={item.company_addresses ?? []}
                  displayField="addressline1"
                  onAdd={() => { setCompanyAddress(newCompanyAddress()); changeTab(3) }}
                  onEdit={(id) => { setCompanyAddress(item.company_addresses.find(u => u.id === id)); changeTab(3) }}
                  onDelete={(id) => alert("delete not implemented")}
                  onPrimaryCahnge={(id) => console.log("setting primary to", id)}
                  primaryField="primary_address"
                />
              </>
            </CenterSpinner>
            {props.onClose && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', p: 2 }}>
                <Button variant='contained' color='darkgrey' onClick={props.onClose}>Lukk</Button>
              </Box>
            )}
          </Box>
        </TabPanel>
        <TabPanel value={tabIndex} index={1} sx={{ minHeight: 200 }}>
          <Box sx={{ display: 'flex', flexDirection: "column", justifyContent: 'center', gap: 3, maxWidth: { xs: "100%", md: 1200 } }}>
            <CompanyForm item={item} error={getError} ref={addCompanyRef} />
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button variant='contained' color='darkgrey' onClick={reset}>Tilbakestill</Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant='contained'
                  color='darkgrey'
                  onClick={() => {
                    if (changeTab(0, false)) {
                      changeTab(0);
                    } else if (props.onClose) {
                      props.onClose();
                    }
                  }}
                >
                  Avbryt
                </Button>
                <Button
                  variant='contained'
                  color='info'
                  onClick={saveItem}
                  disabled={isLoading}
                  endIcon={isLoading && <CircularProgress size={22} />}
                >
                  {item.id ? "Lagre" : "Opprett"}
                </Button>
              </Box>
            </Box>
          </Box>
        </TabPanel>
        <TabPanel value={tabIndex} index={2} sx={{ minHeight: 200 }}>
          <>
            <ManyToManyForm
              item={{
                select_id: companyContact.contact_id,
              }}
              selectItemName="Kontaktperson"
              selectTable="contact"
              ref={addCompanyContactRef}
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setCompanyContact(newCompanyContact()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveCompanyContact}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {companyContact.id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Kontaktpersoner"}
              data={item.company_contacts ?? []}
              displayField="contact_name"
              onAdd={() => { setCompanyContact(newCompanyContact()) }}
              onEdit={(id) => { setCompanyContact(item.company_contacts.find(u => u.id === id)) }}
            />
          </>
        </TabPanel>
        <TabPanel value={tabIndex} index={3} sx={{ minHeight: 200 }}>
          <>
            <AddressForm
              item={{
                id: companyAddress.address_id,
                name: "",
                refrence_id: companyAddress.company_id,
                valid_from: companyAddress.valid_from,
                valid_to: companyAddress.valid_to,
                primary: companyAddress.primary_address,
                addressline1: companyAddress.addressline1,
                addressline2: companyAddress.addressline2,
                addressline3: companyAddress.addressline3,
                postcode: companyAddress.postcode,
                postoffice: companyAddress.postoffice,
              }}
              checkPrimary
              fromAndToDate
              ref={addAddressRef}
            />
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Button variant='contained' color='darkgrey' onClick={() => { changeTab(0); setCompanyAddress(newCompanyAddress()) }}>Avbryt</Button>
              <Button
                variant='contained'
                color='info'
                onClick={saveCompanyAddress}
                disabled={isLoading}
                endIcon={isLoading && <CircularProgress size={22} />}
              >
                {companyAddress.address_id ? "Lagre" : "Opprett"}
              </Button>
            </Box>
            <DataListInfoTable title={"Adresser"}
              data={item.company_addresses ?? []}
              displayField="addressline1"
              onAdd={() => setCompanyAddress(newCompanyAddress())}
              onEdit={(id) => setCompanyAddress(item.company_addresses.find(u => u.id === id))}
            />
          </>
        </TabPanel>
      </Box>
    </>
  )
}